import { Calendar, Clock, Plus, Trash2, X, Mail } from "lucide-react";
import { useEffect, useState } from "react";

const MeetingConfirmed = ({
  data,
  eventData,
  onReschedule,
  onCancel,
  onInviteMore,
  rescheduled,
}) => {
  const { demoData, selectedTime, selectedTimeZone, lead_owner } = data;

  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [guestEmails, setGuestEmails] = useState([""]);
  const [emailErrors, setEmailErrors] = useState([""]);

  const [attendees, setAttendees] = useState([]);

  useEffect(() => {
    setAttendees(eventData?.attendees);
  }, [eventData]);

  const addEmailField = () => {
    setGuestEmails([...guestEmails, ""]);
    setEmailErrors([...emailErrors, ""]);
  };

  const removeEmailField = (index) => {
    if (guestEmails.length > 1) {
      setGuestEmails(guestEmails.filter((_, i) => i !== index));
      setEmailErrors(emailErrors.filter((_, i) => i !== index));
    }
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return "Email Required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Invalid Email";
    }
    return "";
  };

  const updateEmail = (index, value) => {
    const updatedEmails = [...guestEmails];
    updatedEmails[index] = value;
    setGuestEmails(updatedEmails);

    // Validate email and update errors
    const updatedErrors = [...emailErrors];
    updatedErrors[index] = validateEmail(value);
    setEmailErrors(updatedErrors);
  };

  const handleInvite = () => {
    // Validate all emails before inviting
    const updatedErrors = guestEmails.map((email) => validateEmail(email));
    setEmailErrors(updatedErrors);

    // Check if there are any errors
    const hasErrors = updatedErrors.some((error) => error !== "");

    if (!hasErrors) {
      const validEmails = guestEmails.filter((email) => email.trim() !== "");
      console.log("Inviting guests:", validEmails);
      onInviteMore(validEmails);

      closePopup();
    }
  };

  const handleCancel = () => {
    closePopup();
  };

  const openPopup = () => {
    setShowInvitePopup(true);
    setTimeout(() => setIsAnimating(true), 10);
  };

  const closePopup = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setShowInvitePopup(false);
      setGuestEmails([""]);
    }, 300);
  };

  // Format the date and time from selectedTime
  const formatDateTime = ({ start_iso, display_start, display_end }) => {
    const [datePart, timePart] = display_start.split(/(?<=\d{4}) /);

    const date = new Date(start_iso);

    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });

    return {
      dateStr: datePart,
      weekday,
      timeRange: `${timePart} - ${display_end}`,
    };
  };

  const { dateStr, weekday, timeRange } = formatDateTime(selectedTime);

  return (
    <div className="px-4">
      {/* Main Content Card */}

      {eventData?.meeting_created === false ? (
        <div className="max-w-md mx-auto p-4">
          <div className="relative bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-blue-400 to-indigo-500"></div>

            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <Clock className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="absolute inset-0 w-16 h-16 rounded-full border-2 border-blue-200 animate-ping"></div>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
                Meeting Request Received
              </h3>

              <p className="text-gray-600 text-center mb-4 leading-relaxed">
                Thank you for your interest! Your meeting details have been
                submitted successfully.
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-4 border border-blue-100">
                <p className="text-gray-800 font-semibold text-center">
                  We're currently reviewing your request and will confirm the
                  meeting shortly.
                </p>
              </div>

              <div className="flex items-center justify-center text-gray-500 text-sm">
                <Mail className="w-4 h-4 mr-2" />
                You'll receive a confirmation via email soon.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="overflow-hidden mb-4">
            {/* Confirmation Section */}
            <div className="p-6 text-center">
              {/* Checkmark Icon */}
              <div className="w-12 h-12 mb-4 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg
                  className="w-6 h-6 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <div className="text-center">
                <h2 className="text-base font-medium text-gray-600 mb-1">{`Your meeting is ${
                  rescheduled ? "rescheduled" : "scheduled"
                }`}</h2>
                <p className="text-sm text-gray-500 mb-1">with</p>
                <h1 className="text-xl font-bold text-gray-900 mb-4">
                  {/* Silas Hatton */}
                  {lead_owner?.name ? lead_owner?.name : "Silas Hatton"}
                </h1>
              </div>
            </div>

            {/* Separator */}
            <div className="h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent mx-6"></div>

            {/* Meeting Details */}
            <div className="p-2 py-6">
              <div className="flex">
                {/* Date Section */}
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold text-gray-900 truncate text-left">
                      {dateStr}
                    </div>
                    <div className="text-xs text-gray-500 text-left">
                      {weekday}
                    </div>
                  </div>
                </div>
                <div className="w-px h-12 bg-gradient-to-b from-transparent via-blue-200 to-transparent mx-4"></div>

                {/* Time Section */}
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold text-gray-900 truncate text-left">
                      {timeRange}
                    </div>
                    <div className="text-xs text-gray-500 text-left">
                      {selectedTimeZone.label}
                    </div>
                  </div>
                </div>
              </div>

              {/* Reschedule Button */}
              {!rescheduled && (
                <button
                  className="w-full mt-4 bg-white border-2 border-blue-600 text-blue-600 font-medium py-2.5 px-4 rounded-md hover:bg-blue-700 hover:text-white transition-colors text-sm"
                  onClick={onReschedule}
                >
                  Reschedule
                </button>
              )}
              {rescheduled && (
                <button
                  className="w-full mt-4 bg-gray-400 text-white rounded-md font-medium hover:bg-gray-500 py-2.5 px-4  transition-colors text-sm"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Participants Section */}
          <div className="mb-6">
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3 text-left">
              PARTICIPANTS
            </h3>

            <div className="space-y-3">
              <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 flex items-center">
                <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 flex-shrink-0">
                  {(lead_owner?.name?.charAt(0) || "S").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="font-semibold text-gray-900 text-sm truncate">
                    {/* Silas Hatton */}
                    {lead_owner?.name ? lead_owner?.name : "Silas Hatton"}
                  </div>
                  <div className="text-gray-500 text-xs truncate">
                    {/* silas@eventify.io */}
                    {lead_owner?.email
                      ? lead_owner?.email
                      : "silas@eventify.io"}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 flex-shrink-0">
                      {`${demoData.firstName.charAt(0).toUpperCase()}`}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="font-semibold text-gray-900 text-sm truncate">
                        {`${demoData.firstName} ${demoData.lastName}`}
                      </div>
                      <div className="text-gray-500 text-xs truncate">
                        {demoData?.email || "hussain@eventify.io"}
                      </div>
                    </div>
                  </div>
                </div>
                {!rescheduled && attendees?.length === 1 && (
                  <button
                    className="w-full bg-white border-2 border-blue-600 text-blue-600 font-medium py-2 px-4 rounded-lg hover:bg-blue-700 hover:text-white transition-colors text-sm"
                    onClick={openPopup}
                  >
                    Invite Guest
                  </button>
                )}
              </div>
              {attendees?.length > 1 &&
                attendees?.slice(1).map((attendee) => (
                  <div
                    className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 flex items-center"
                    key={attendee.email}
                  >
                    <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 flex-shrink-0">
                      {`${attendee.email.charAt(0).toUpperCase()}`}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-gray-500 text-xs truncate">
                        {attendee.email}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Invite Guest Popup */}
          {showInvitePopup && (
            <div
              className={`fixed inset-0 bg-black transition-opacity duration-300 ease-out flex items-center justify-center p-4 z-50 ${
                isAnimating ? "bg-opacity-30" : "bg-opacity-0"
              }`}
            >
              <div
                className={`bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 transform transition-all duration-300 ease-out ${
                  isAnimating ? "scale-100 opacity-100" : "scale-95 opacity-0"
                }`}
              >
                {/* Popup Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Invite Guests
                  </h3>
                  <button
                    onClick={handleCancel}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Popup Content */}
                <div className="p-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Add guest email addresses
                  </p>

                  <div className="space-y-4 mb-4">
                    {guestEmails.map((email, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex items-start space-x-2">
                          <div className="flex-1">
                            <input
                              type="email"
                              value={email}
                              onChange={(e) =>
                                updateEmail(index, e.target.value)
                              }
                              placeholder="Enter email address"
                              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-colors ${
                                emailErrors[index]
                                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                                  : "border-gray-300 focus:ring-blue-600 focus:border-transparent"
                              }`}
                            />
                            {emailErrors[index] && (
                              <p className="text-red-500 text-xs mt-1 text-left">
                                {emailErrors[index]}
                              </p>
                            )}
                          </div>
                          {guestEmails.length > 1 && (
                            <button
                              onClick={() => removeEmailField(index)}
                              className="text-red-500 hover:text-red-700 transition-colors p-1 mt-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add More Button */}
                  <button
                    onClick={addEmailField}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors text-sm mb-6"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add another email</span>
                  </button>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button
                      onClick={handleCancel}
                      className="flex-1 bg-gray-400 text-white rounded-md font-medium hover:bg-gray-500 py-2.5 px-4  transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleInvite}
                      className="flex-1 bg-blue-600 text-white font-medium py-2.5 px-4 rounded-md hover:bg-blue-700 transition-colors text-sm"
                    >
                      Invite
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MeetingConfirmed;
